import React, { useState, useEffect, useMemo } from "react";
import { Table } from "react-bootstrap";
import { getLocalidades, deleteLocalidad } from "../helpers/rutaLocalidades";
import { getDeptos } from "../helpers/rutaDeptos";
import { getProvincias } from "../helpers/rutaProvincias";
import ModalLocalidad from "./ModalLocalidad";
import "../css/tableclientes.css";

const TableLocalidades = () => {
  const [localidades, setLocalidades] = useState({
    data: { localidades: [] },
    loading: true,
    error: "",
  });

  const [deptos, setDeptos] = useState({
    data: { deptos: [] },
    loading: true,
    error: "",
  });

  const [provincias, setProvincias] = useState({
    data: { provincias: [] },
    loading: true,
    error: "",
  });

  const [localidadSeleccionada, setLocalidadSeleccionada] = useState(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    consultaTodo();
  }, []);

  const consultaTodo = async () => {
    try {
      const [datosLocalidades, datosDeptos, datosProvincias] = await Promise.all([
        getLocalidades(),
        getDeptos(),
        getProvincias(),
      ]);

      setLocalidades({
        data: datosLocalidades || { localidades: [] },
        loading: false,
        error: "",
      });

      setDeptos({
        data: datosDeptos || { deptos: [] },
        loading: false,
        error: "",
      });

      setProvincias({
        data: datosProvincias || { provincias: [] },
        loading: false,
        error: "",
      });
    } catch (e) {
      console.error("❌ Error al cargar datos:", e);

      setLocalidades((s) => ({
        ...s,
        loading: false,
        error: e?.message || "Error al cargar localidades",
      }));

      setDeptos((s) => ({
        ...s,
        loading: false,
        error: e?.message || "Error al cargar deptos",
      }));

      setProvincias((s) => ({
        ...s,
        loading: false,
        error: e?.message || "Error al cargar provincias",
      }));
    }
  };

  const handleClose = () => {
    setShow(false);
    consultaTodo();
  };

  const handleShow = () => setShow(true);

  const modificaLocalidad = (localidad) => {
    setLocalidadSeleccionada(localidad);
    handleShow();
  };

  const eliminarLocalidad = (id) => {
    if (window.confirm("¿Está seguro que desea borrar la localidad?")) {
      deleteLocalidad(id).then(() => consultaTodo());
    }
  };

  const deptosMap = useMemo(() => {
    const arr = Array.isArray(deptos.data?.deptos) ? deptos.data.deptos : [];
    const map = {};
    arr.forEach((d) => {
      if (d?.coddept != null) {
        map[String(d.coddept)] = d.depto || "-";
      }
    });
    return map;
  }, [deptos]);

  const provinciasMap = useMemo(() => {
    const arr = Array.isArray(provincias.data?.provincias)
      ? provincias.data.provincias
      : [];
    const map = {};
    arr.forEach((p) => {
      if (p?.codprov != null) {
        map[String(p.codprov)] = p.provincia || "-";
      }
    });
    return map;
  }, [provincias]);

  if (localidades.loading || deptos.loading || provincias.loading) {
    return <div className="p-3">Cargando localidades…</div>;
  }

  if (localidades.error) return <div className="p-3 text-danger">{localidades.error}</div>;
  if (deptos.error) return <div className="p-3 text-danger">{deptos.error}</div>;
  if (provincias.error) return <div className="p-3 text-danger">{provincias.error}</div>;

  const rows = (localidades.data?.localidades || [])
    .filter((l) => l?.activo ?? true)
    .map((l) => {
      const nombreDepto =
        l?.depto?.depto ||
        deptosMap[String(l?.coddept)] ||
        "-";

      const nombreProvincia =
        l?.provincia?.provincia ||
        provinciasMap[String(l?.codprov)] ||
        "-";

      const codigoPostal =
        l?.codpostal ??
        l?.codigopostal ??
        "-";

      return {
        ...l,
        nombreDepto,
        nombreProvincia,
        codigoPostal,
      };
    })
    .sort((a, b) => {
      const A = a?.localidad?.toString().trim() || "";
      const B = b?.localidad?.toString().trim() || "";

      if (!A && !B) return 0;
      if (!A) return 1;
      if (!B) return -1;

      const cmpLoc = A.localeCompare(B, "es", { sensitivity: "base" });
      if (cmpLoc !== 0) return cmpLoc;

      return (a?.nombreProvincia || "").localeCompare(
        b?.nombreProvincia || "",
        "es",
        { sensitivity: "base" }
      );
    });

  return (
    <div className="table-container">
      <div className="col-12 mt-4"></div>

      <Table striped bordered hover>
        <thead>
          <tr className="header">
            <th className="borrar">Acciones</th>
            <th>Localidad</th>
            <th>Departamento</th>
            <th>Provincia</th>
            <th>Código Postal</th>
          </tr>
        </thead>

        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={5} className="text-center">
                No hay localidades.
              </td>
            </tr>
          ) : (
            rows.map((localidad) => (
              <tr key={localidad._id}>
                <td className="borrar" style={{ whiteSpace: "nowrap" }}>
                  <div className="d-flex align-items-center justify-content-center gap-2 flex-nowrap">
                    <button
                      className="btn btn-primary"
                      onClick={() => modificaLocalidad(localidad)}
                    >
                      <i className="fa fa-pencil-square-o" />
                    </button>

                    <button
                      className="btn btn-danger"
                      onClick={() => eliminarLocalidad(localidad._id)}
                    >
                      <i className="fa fa-trash-o" />
                    </button>
                  </div>
                </td>

                <td>{localidad.localidad || "-"}</td>
                <td>{localidad.nombreDepto}</td>
                <td>{localidad.nombreProvincia}</td>
                <td>{localidad.codigoPostal}</td>
              </tr>
            ))
          )}
        </tbody>
      </Table>

      <ModalLocalidad
        show={show}
        handleClose={handleClose}
        localidad={localidadSeleccionada}
      />
    </div>
  );
};

export default TableLocalidades;